import React, {
  forwardRef,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  SVGProps
} from 'react'
import { FieldErrors } from 'react-hook-form'
import { cn } from '../../utils'
import { getErrorByPath } from './getErrorByPath'
import FormErrorMessage from './FormErrorMessage'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  type?: HTMLInputTypeAttribute
  name?: string
  pattern?: string
  maxLength?: number
  variant?: 'semitransparent' | 'white' | 'solid' | 'outline'
  label?: string
  icon?: React.FC<SVGProps<SVGSVGElement>>
  errors?: FieldErrors
  oversize?: boolean
  onClick?: () => void
  activateEmptyWarning?: boolean
  'data-testid': string
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      name,
      label,
      type = 'text',
      variant = 'solid',
      icon: IconComponent,
      errors,
      oversize,
      onClick,
      className,
      value,
      readOnly = false,
      disabled = false,
      activateEmptyWarning = false,
      'data-testid': testId,
      ...props
    },
    ref
  ) => {
    const { pattern, maxLength } = props
    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '')

    const inputClassNames = cn([
      'elm-input',
      '!mt-0',
      { 'min-w-[330px]': oversize },
      { 'pointer-events-none': readOnly },
      'dark:!bg-cool-gray-700',
      'dark:!text-cool-gray-200',
      'dark:caret-red-400',
      'dark:!border-b-cool-gray-200',
      'dark:aria-[invalid=true]:!border-b-red-400'
    ])

    const labelClassNames = cn([
      'elm-label',
      'dark:!text-cool-gray-200',
      { 'pointer-events-none': readOnly }
    ])

    const isInvalid = !!getErrorByPath(errors, name)

    return (
      <div
        className={cn(className)}
        data-testid={`${testId}-wrapper`}
        aria-readonly={readOnly ? 'true' : 'false'}
      >
        <div className="relative">
          <input
            id={id}
            name={name}
            className={cn(
              inputClassNames,
              activateEmptyWarning &&
                isEmpty &&
                !disabled &&
                'ring-2 ring-red-400 rounded-md',
              'aria-[invalid=true]:!border-b-red-500'
            )}
            data-variant={variant}
            aria-invalid={isInvalid}
            aria-readonly={readOnly ? 'true' : 'false'}
            aria-labelledby={label ? `${id}-label` : undefined}
            pattern={pattern}
            maxLength={maxLength}
            onClick={onClick}
            type={type}
            readOnly={readOnly}
            disabled={disabled}
            tabIndex={readOnly ? -1 : undefined}
            aria-label={label ? undefined : (props['aria-label'] ?? name)}
            {...props}
            ref={ref}
            data-testid={testId}
            value={value}
          />
          {label && (
            <label
              className={labelClassNames}
              htmlFor={id}
              id={`${id}-label`}
              data-testid={`${testId}-label`}
            >
              {label}
            </label>
          )}
          {IconComponent && (
            <IconComponent
              width={24}
              className="block absolute right-1 top-0.5"
              data-testid={`${testId}-icon`}
              aria-hidden="true"
            />
          )}
        </div>
        {name && errors && (
          <FormErrorMessage
            errors={errors}
            name={name}
            data-testid={`${testId}-error-message`}
          />
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
